import type { PolicyAssignment, PolicyDryRun, RuntimePolicy, RuntimePolicyBody } from '@ralph/contracts'
import { ApiRequestError, api, apiGet, apiSend } from './client.ts'

/**
 * The policy plane's client (PRD-18 AGM-100…106).
 *
 * Every call here is administrator-only by construction, and that is the point of the
 * module boundary: the maker client (`api/maker.ts`) cannot reach a policy value even by
 * accident, because it has no function that names one. The integrity rule the PRD states as
 * "the floor is unreachable from below" is enforced server-side; keeping the two client
 * modules disjoint is how it stays legible on this side.
 *
 * // WIRE: D-api owns `apps/api/src/routes/admin/policies.ts`. Envelopes:
 * //   GET    /api/admin/policies                 → {items: RuntimePolicy[]}
 * //   GET    /api/admin/policies/:id             → RuntimePolicy
 * //   POST   /api/admin/policies                 UpsertRuntimePolicyRequest → RuntimePolicy
 * //   PUT    /api/admin/policies/:id             UpsertRuntimePolicyRequest, If-Match: <version> → RuntimePolicy
 * //   DELETE /api/admin/policies/:id             → 200|204; 409 when any assignment references it (AGM-105)
 * //   POST   /api/admin/policies/:id/dry-run     UpsertRuntimePolicyRequest → PolicyDryRun (zero writes, AGM-106)
 * //   GET    /api/admin/policies/assignments     → {items: PolicyAssignment[]}
 * //   PUT    /api/admin/agents/:agentId/policy   {policyId} → 200
 * //   DELETE /api/admin/agents/:agentId/policy   → 200|204 (falls back to the builtin profile)
 * // The 409 body SHOULD carry the referencing agents in its message — the panel renders the
 * // server's message verbatim under "this profile is still in use", and a bare "conflict"
 * // leaves the administrator with no way to find the agents they must reassign first.
 */

export type UpsertPolicyInput = {
  name: string
  description: string
  body: RuntimePolicyBody
  note: string
}

export const fetchPolicies = (): Promise<{ items: RuntimePolicy[] }> =>
  apiGet<{ items: RuntimePolicy[] }>('/api/admin/policies')

export const fetchPolicy = (policyId: string): Promise<RuntimePolicy> =>
  apiGet<RuntimePolicy>(`/api/admin/policies/${encodeURIComponent(policyId)}`)

export const createPolicy = (input: UpsertPolicyInput): Promise<RuntimePolicy> =>
  apiSend<RuntimePolicy>('/api/admin/policies', 'POST', input)

/**
 * Saves an existing profile.
 *
 * `If-Match` carries the version the edit was based on, exactly as the agent definition PUT
 * does. A profile is read by every agent that resolves through it, so two administrators
 * editing the same one is not a hypothetical — and a lost update here silently changes the
 * runtime of every one of those agents.
 */
export const updatePolicy = (policyId: string, version: number, input: UpsertPolicyInput): Promise<RuntimePolicy> =>
  api<RuntimePolicy>(`/api/admin/policies/${encodeURIComponent(policyId)}`, {
    method: 'PUT',
    headers: { 'if-match': String(version) },
    body: JSON.stringify(input)
  })

export const deletePolicy = (policyId: string): Promise<unknown> =>
  apiSend<unknown>(`/api/admin/policies/${encodeURIComponent(policyId)}`, 'DELETE')

/**
 * What an edit would do, before it does it (AGM-106).
 *
 * Takes the DRAFT body rather than a stored id: the question the administrator is asking is
 * "what happens if I save this", and answering it from the stored row would preview the
 * change they already made rather than the one they are about to.
 */
export const dryRunPolicy = (policyId: string, input: UpsertPolicyInput): Promise<PolicyDryRun> =>
  apiSend<PolicyDryRun>(`/api/admin/policies/${encodeURIComponent(policyId)}/dry-run`, 'POST', input)

export const fetchPolicyAssignments = (): Promise<{ items: PolicyAssignment[] }> =>
  apiGet<{ items: PolicyAssignment[] }>('/api/admin/policies/assignments')

export const assignPolicy = (agentId: string, policyId: string): Promise<unknown> =>
  api<unknown>(`/api/admin/agents/${encodeURIComponent(agentId)}/policy`, {
    method: 'PUT',
    body: JSON.stringify({ policyId })
  })

/** Clearing an assignment returns the agent to the builtin profile — never to "no profile". */
export const clearPolicyAssignment = (agentId: string): Promise<unknown> =>
  apiSend<unknown>(`/api/admin/agents/${encodeURIComponent(agentId)}/policy`, 'DELETE')

/**
 * A delete the server refused because agents still resolve through the profile (AGM-105).
 *
 * Recognised by status alone: 409 is the only conflict this endpoint has, and matching on a
 * code string would degrade to a generic apology the moment the API spells it differently.
 * The server's own message is rendered next to this, because it is the half that names the
 * agents to reassign.
 */
export const isPolicyInUse = (error: unknown): boolean => error instanceof ApiRequestError && error.status === 409

/** A concurrent edit landed first: 412 through the API, 409 through some proxies. */
export const isPolicyVersionConflict = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.status === 412 || error.code === 'admin_version_conflict')
